/**
 * The wizard's step arrow.
 *
 * A SOLID triangle built from borders, not a rotated 2px corner: the corner shape read as
 * a stray grey chevron floating between the labels (owner, 2026-08-23) because at 7px a
 * hairline outline carries almost no ink.
 *
 * TWO MEASURED CORRECTIONS, 2026-08-23 (owner: "箭头的细节不对！！！！"). Read off the
 * screenshot at 2x and halved:
 *
 *   shaft            2px tall, y 70..73 (2x) → centre 71.5
 *   head back edge  10px tall, y 64..83 (2x) → centre 73.5
 *
 * 1. THE HEAD SAT 1px BELOW THE LINE. MUI's `.MuiStepConnector-line` is a span with
 *    `height: 0` — the line IS its `border-top`, drawn downward from the box's top
 *    edge. `top: 50%` therefore resolved to the border's TOP EDGE, while the shaft's
 *    own centre is half a border-width lower. The offset was exactly
 *    `WIZARD_STEP_SHAFT_WIDTH / 2`, which is why it measured as 1px on a 2px line.
 *
 * 2. THE HEAD WAS 5× THE SHAFT. A 10px back edge on a 2px line leaves 4px shoulders
 *    at the join. At 3:1 the shoulders are 2px — the shaft's own weight — and the
 *    two read as one stroke with a direction.
 *
 * Both numbers now derive from ONE shaft width, which `ProcessDesigner` also uses for
 * `borderTopWidth`. Typing 2 in both places is how the head went off-axis in the
 * first place (svdy-ui §17).
 *
 * Only PHYSICAL properties are written. `stylis-plugin-rtl` mirrors `right` → `left`
 * and `border-left` → `border-right` in one pass, so there is no `isRtl` branch to get
 * backwards and no `transform` rotation (which the plugin does NOT mirror).
 */

/** The connector line's thickness. The arrow's offset is derived from it. */
export const WIZARD_STEP_SHAFT_WIDTH = 2;

export const WIZARD_STEP_ARROW_LENGTH = 6;
export const WIZARD_STEP_ARROW_HALF_HEIGHT = 3;
export const WIZARD_STEP_ARROW_OFFSET = -1;

/**
 * NEGATIVE, and the sign is the whole fix (owner: "这个箭头又坏了", 2026-08-24).
 *
 * Measured off the reported screenshot by decoding the PNG, at 1x (the step circle is 24
 * image px and MUI's StepIcon is 24 CSS px):
 *
 *   shaft      y 87..88  → centre 88.0
 *   arrowhead  y 87..92  → centre 89.5, tip pixel at y=89
 *
 * The head hung 2px BELOW the line and was entirely one-sided — its top edge level with the
 * line's top, then 4px of ink below the line's bottom. It read as a flag on a pole rather
 * than an arrow on a stroke.
 *
 * WHY, and the previous round had the direction backwards. An absolutely-positioned child
 * resolves `top` against its containing block's PADDING box. `.MuiStepConnector-line` has
 * `height: 0` and `border-top: 2px`, so its padding box begins BELOW that border — at
 * `outer + 2`, not `outer`. With the 6px triangle centred by `translateY(-50%)`:
 *
 *   top: 50%  → 50% of a 0-height padding box = 0 → head centre `outer + 2` → 1px low
 *   top: +1   → head centre `outer + 3`                                    → 2px low
 *   top: -1   → head centre `outer + 1` = the shaft's own centre           → 0 ✓
 *
 * So the earlier correction identified the right mechanism (`height: 0` makes a percentage
 * meaningless) and then moved the head the WRONG WAY, doubling the error from 1px to 2px.
 * That is why it read as fixed against a 1px complaint and returned as a 2px one.
 *
 * Both existing tests passed throughout, because they asserted this constant's VALUE rather
 * than the rendered geometry — `expect(style.top).toBe(WIZARD_STEP_SHAFT_WIDTH / 2)` pinned
 * the wrong answer and looked like verification. jsdom has no layout engine, so the guard
 * here is the SIGN plus this derivation; the pixel check belongs in a real browser.
 */
export const WIZARD_STEP_ARROW_TOP = -(WIZARD_STEP_SHAFT_WIDTH / 2);

export function wizardStepArrowStyle() {
  return {
    content: '""',
    position: 'absolute' as const,
    // MINUS half the shaft. `top` resolves against the line's PADDING box, which begins
    // below its 2px border-top, so a POSITIVE offset pushes the head further below the
    // stroke it is meant to sit on.
    top: WIZARD_STEP_ARROW_TOP,
    right: WIZARD_STEP_ARROW_OFFSET,
    width: 0,
    height: 0,
    transform: 'translateY(-50%)',
    borderTop: `${WIZARD_STEP_ARROW_HALF_HEIGHT}px solid transparent`,
    borderBottom: `${WIZARD_STEP_ARROW_HALF_HEIGHT}px solid transparent`,
    borderLeft: `${WIZARD_STEP_ARROW_LENGTH}px solid currentColor`,
  };
}
